import React from 'react'
// libs
import { useRouter } from 'hooks/useRouter'
import { useMutation } from '@apollo/client/react'
// hooks
import { useCurrentUser } from 'hooks/useCurrentUser'
// graphql
import { USER_UPDATE_MUTATION } from 'graphql/user/mutations'
// components
import UserInformationForm from './Form'
// constants
import { LANGUAGE_OPTIONS } from 'constants/index'
import { AUTH_METHOD_OPTIONS, AUTH_METHODS, EMPLOYMENT_STATUS_OPTIONS, USER_GENDER_OPTIONS } from 'constants/users'
// types
import { IUser } from 'types/graphql'
// utils
import omit from 'lodash/omit'
import { generateTimezoneOptions } from 'utils/date'
import { getOptionByValue, getValueFromOption, getValuesFromOptions } from 'utils/form'
import { normalizePhone, toInternationalPhone } from 'utils/validators'

const parseFromApi = (user: IUser) => {
  const values: any = {
    id: user.id,
    dateOfBirth: user.dateOfBirth,
    active: user.active,
    firstName: user.firstName,
    lastName: user.lastName,
    middleName: user.middleName,
    displayName: user.displayName,
    gender: getOptionByValue(user.gender, USER_GENDER_OPTIONS),
    timezone: getOptionByValue(user.timezone, generateTimezoneOptions()),
    email: user.email,
    alternateEmail: user.alternateEmail,
    mobilePhone: normalizePhone(user.mobilePhone),
    homePhone: normalizePhone(user.homePhone),
    workPhone: normalizePhone(user.workPhone),
    language: getOptionByValue(user.language, LANGUAGE_OPTIONS),
    address: user.geoLocation ? { value: omit(user.geoLocation, '__typename') } : undefined,
    avatarUrl: user.avatarUrl,
    code: user.code,
    employmentStatus: getOptionByValue(user.employmentStatus, EMPLOYMENT_STATUS_OPTIONS),
    username: user.username,
    totpEnabled: user.totpEnabled,
    authMethod: getOptionByValue(user.authMethod || AUTH_METHODS.APPLICATION_METHOD, AUTH_METHOD_OPTIONS),
    externalEmployeeId: user.externalEmployeeId,
    activeDirectoryId: user.activeDirectoryId,
    activeDirectoryTenantId: user.activeDirectoryTenantId,
    activeDirectoryAdministrativeUnits: user.activeDirectoryAdministrativeUnits.map((item) => ({
      label: item.displayName,
      value: item.id
    })),
    refreshTokenAbsoluteTtl: user.refreshTokenAbsoluteTtl,
    idleTimeoutSeconds: user.idleTimeoutSeconds,
    // Display only — omitted from the mutation in updateUser below.
    integrations: user.integrations
  }

  if (user.reportsTo) {
    values.reportsTo = {
      label: `${user.reportsTo.firstName} ${user.reportsTo.lastName}`,
      value: user.reportsTo.id
    }
  }

  if (user.positions) {
    values.positions = user.positions.map(({ id, description }) => ({ value: id, label: description }))
  }

  if (user.businessUnits) {
    values.businessUnits = user.businessUnits.map(({ id, description }) => ({ value: id, label: description }))
  }

  if (user.locations) {
    values.locationIds = user.locations.map(({ id, description }) => ({ value: id, label: description }))
  }

  return values
}

interface Props {
  user: IUser
}

const User: React.FC<Props> = ({ user }) => {
  const router = useRouter()

  const { isAdmin } = useCurrentUser()

  const [updateUserMutation] = useMutation(USER_UPDATE_MUTATION)

  const updateUser = async (data: Record<string, any>) => {
    const values = {
      ...omit(data, [
        'id',
        'address',
        'gender',
        'timezone',
        'language',
        'reportsTo',
        'positions',
        'authMethod',
        'businessUnits',
        'employmentStatus',
        'activeDirectoryId',
        'activeDirectoryTenantId',
        'activeDirectoryAdministrativeUnits',
        'integrations'
      ]),
      geoLocation: data.address?.value ? omit(data.address.value, '__typename') : undefined,
      mobilePhone: toInternationalPhone(data.mobilePhone),
      homePhone: toInternationalPhone(data.homePhone),
      workPhone: toInternationalPhone(data.workPhone),
      alternateEmail: data.alternateEmail ? data.alternateEmail : undefined,
      // Unique column: a cleared field must go as null, not '', or two blank codes would collide.
      code: data.code || null,
      reportsToId: getValueFromOption(data.reportsTo),
      positionIds: getValuesFromOptions(data.positions),
      businessUnitIds: getValuesFromOptions(data.businessUnits),
      locationIds: getValuesFromOptions(data.locationIds),
      gender: getValueFromOption(data.gender),
      timezone: getValueFromOption(data.timezone),
      language: getValueFromOption(data.language),
      employmentStatus: getValueFromOption(data.employmentStatus),
      authMethod: getValueFromOption(data.authMethod),
      ...(isAdmin && {
        refreshTokenAbsoluteTtl: data.refreshTokenAbsoluteTtl ? Number(data.refreshTokenAbsoluteTtl) : null,
        idleTimeoutSeconds: data.idleTimeoutSeconds ? Number(data.idleTimeoutSeconds) : null
      })
    }

    await updateUserMutation({
      variables: {
        id: router.query.id,
        companyId: router.query.companyId,
        user: values
      }
    })

    await router.push('/settings/users')
  }

  const getInitialValues = () => {
    let initialValues = {}

    if (user) {
      initialValues = parseFromApi(user)
    }

    return initialValues
  }

  const onCancelClick = () => {
    router.push('/settings/users')
  }

  return (
    <UserInformationForm updateUser={updateUser} onCancelClick={onCancelClick} initialValues={getInitialValues()} />
  )
}

export default User
